import type { Id, protocol } from "@codesandbox/pitcher-client";
import { useStateTransition } from "@codesandbox/states";
import { useEnvironmentInterface } from "environment-interface";
import { PitcherClientConnectionState } from "environment-interface/pitcher";
import type { Task } from "features/types/task";
import { useTasks } from "hooks/pitcher/useTasks";
import { useUnassignedPorts } from "hooks/pitcher/useUnassignedPorts";
import type { IRange } from "monaco-editor";
import { useEffect, useReducer } from "react";

import { createHookContext } from "utils/createHookContext";
import { executeVSCodeCommand } from "utils/vscode";

import { useLegacyPitcher } from "../utils/usePitcher";
import { useReducerDevtools } from "../utils/useReducerDevtools";

import { reducer } from "./reducer";
import type { State } from "./types";

interface Props {
  initialState?: State;
  /** Ensures that a shell in a terminated state can be rendered */
  shellId?: Id;
}

/**
 * The urls we can rewrite in the terminal.
 */
const LOCALHOST_URLS = [
  "localhost",
  "0.0.0.0", // broadcast IP
  "192.168.241.2", // the IP of the VM
];

export const useShell = createHookContext(({ shellId }: Props) => {
  const pitcher = useLegacyPitcher();
  const { navigation } = useEnvironmentInterface();

  const tasks = useTasks();
  const unassignedPorts = useUnassignedPorts();

  const feature = useReducer(reducer, {
    state:
      pitcher.connectionState === PitcherClientConnectionState.Connected
        ? "INITIALIZING"
        : "CONNECTING",
    shellId,
  });

  useReducerDevtools("shell-" + shellId, feature);

  const [state, dispatch] = feature;

  useEffect(() => pitcher.events.subscribe(dispatch), []);

  // TODO: Implement position (last property) later, out of time
  // https://linear.app/codesandbox/issue/CSB-2516/create-selection-when-a-click-in-the-terminal-has-a-position
  useStateTransition(
    state,
    {
      READY: {
        PATH_CLICK: "READY",
      },
    },
    (_, { path, isAbsolute, position }) => {
      const selections: IRange[] | undefined = position
        ? [
            {
              startLineNumber: position[0],
              endLineNumber: position[0],
              startColumn: position[1],
              endColumn: position[1],
            },
          ]
        : undefined;

      if (isAbsolute) {
        const relativePath = pitcher.fs.getRelativePath(path);

        executeVSCodeCommand("codesandbox.openCsbTab", {
          type: "FILE",
          filepath: relativePath,
          initialSelections: selections,
        });
      } else {
        // A shortcut until the refactor to latest react-states
        const dispose = pitcher.events.subscribe((event) => {
          if (
            event.type === "PITCHER:FS:PATH_SEARCH_SUCCESS" &&
            event.term === path
          ) {
            dispose();
            const match = event.matches[0];

            if (match) {
              const relativePath = pitcher.fs.getRelativePath(match.path);

              executeVSCodeCommand("codesandbox.openCsbTab", {
                type: "FILE",
                filepath: relativePath,
                initialSelections: selections,
              });
            }
          }
        });
        pitcher.fs.pathSearch({ text: path });
      }
    },
  );

  useStateTransition(state, { READY: { URL_CLICK: "READY" } }, (_, { url }) => {
    const isLocalhostUrl = LOCALHOST_URLS.some(
      (localhostUrl) =>
        url.startsWith(localhostUrl) ||
        url.startsWith(`http://${localhostUrl}`) ||
        url.startsWith(`https://${localhostUrl}`),
    );

    try {
      if (isLocalhostUrl) {
        // TODO: make this work even when there is no port open, to make it work more consistently.
        const matchedUrl = getUrlFromDevtools(url, unassignedPorts, tasks);

        if (matchedUrl) {
          navigation.openUrl(matchedUrl);
          return;
        }
      }
    } catch (e) {
      // eslint-disable-next-line
      console.warn("Failed to parse " + url + ", error: " + e);
    }

    navigation.openUrl(url);
  });

  return feature;
});

function getUrlFromDevtools(
  url: string,
  unassignedPorts: protocol.port.Port[],
  tasks: Record<string, Task>,
): string | undefined {
  const urlWithProtocol = url.startsWith("http") ? url : `https://${url}`;
  // Check if this is a link to a running preview devtool
  const port = Number(new URL(urlWithProtocol).port);

  if (Number.isNaN(port)) {
    return;
  }

  const baseUrl = new URL(urlWithProtocol);
  const unassignedPort = unassignedPorts.find((p) => p.port === port);
  if (unassignedPort) {
    return new URL(
      new URL("https://" + unassignedPort.url),
      baseUrl,
    ).toString();
  }

  if (tasks) {
    const taskPort = Object.values(tasks)
      .map((task) => task.ports.find((p) => p.port === port))
      .find(Boolean);

    if (taskPort) {
      return new URL(new URL("https://" + taskPort.url), baseUrl).toString();
    }
  }
}

export const ShellProvider = useShell.Provider;

export const ShellConsumer = useShell.Consumer;

export const ShellContext = useShell.context;
